import React, { useState, useEffect} from 'react';
import { Form, Select, DatePicker, Button, TreeSelect, Typography, Flex } from 'antd';
import { requestPlaces, requestDevices, createSchedule } from '../../../services/UpdateManagerService';
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { useNotificationApi } from '../../UI/NotificationProvider';
import "./Scheduler.css"

const { Option } = Select;
const { Paragraph } = Typography;

const DATE_TIME_FORMAT = 'MM/DD/YYYY HH:mm:ss';

dayjs.extend(utc);

const schedulerCommonProps = {
    rules: [{ required: true, message: 'Required' }],
    style: { flex: 1 }
}

export const Scheduler = ({dataSource, setDataSource, setRefreshTable, refreshHandler, ...props}) => {

    const minTime = dayjs().add(2, 'minute');
    const notificationApi = useNotificationApi();
    const [form] = Form.useForm();
    const [customers, setCustomers] = useState([]);
    const [sites, setSites] = useState([]);
    const [devices, setDevices] = useState([]);
    const [scheduledDate, setScheduledDate] = useState(dayjs().add(2, 'minute'));
    const [displayQuickSelect, setDisplayQuickSelect] = useState(true);
    const [panelDate, setPanelDate] = useState(dayjs());
    const [value, setValue] = useState(undefined);

    const[openDatePicker, setOpenDatePicker] = useState(false);

    useEffect(() => {
        setCustomers(dataSource.users);
    }, [dataSource,scheduledDate, customers]);

    useEffect(() => {
        if(panelDate && panelDate.isSame(dayjs(), "day")){
            setDisplayQuickSelect(true);
        } else {
            setDisplayQuickSelect(false);
        }
    }, [panelDate])


    const createTree = (data) => {
        let devices = [];
        // Group by deviceType
        for(const {deviceType, serialNum, id, comment} of data) {
            if(!devices[deviceType]) devices[deviceType] = [];
            let title = ( comment !== null && comment.length > 0 ) ? serialNum + ` (${comment})` : serialNum;
            devices[deviceType].push({ title, value: serialNum});
        }
        // Generate Tree
        let tree = [];
        Object.entries(devices).forEach(entry => {
            const [key, value] = entry;
            tree.push({
                title: (key === 'null') ? 'Other' : key,
                value: (key === 'null') ? 'Other' : key,
                disabled: true,
                children: value
            })
        });
        setDevices(tree);
    }

    const onTreeChange = (value, label, extra) => {
        setValue(value);
    };

    const onFinish = (data) => {
        let utcScheduledDateTime = dayjs(data.dateTime).utc().toISOString();

        Object.assign(data, {scheduledDateTime: utcScheduledDateTime});

        const getCompanyByUsername = (username) => {
            const customer = customers.find(customer => customer.username === username);
            return customer? customer.companyName : null;
        }

        createSchedule(data).then( function( response) {
            if (response) {
                const successDevices = response.filter(schedule => schedule.created).map(schedule => schedule.device);
                const failedDevices = response.filter(schedule => !schedule.created).map(schedule => schedule.device);
                
                form.resetFields();
                refreshHandler();
                setTimeout(() => {
                    if(successDevices.length > 0){
                        notificationApi['success']({
                            message: 'Schedule created.',
                            description: (<>
                                <Paragraph>
                                    Customer: {getCompanyByUsername(data.customer)}<br/>
                                    Device{successDevices.length > 1 ? 's' :''}: {successDevices.join(', ')}<br/>
                                    Scheduled date & time: {dayjs(data.dateTime).format(DATE_TIME_FORMAT)}
                                </Paragraph>
                            </>),
                            duration: 12,
                            style: {
                                width: 500,
                            },
                        });
                    } 
                    if(failedDevices.length > 0) {
                        notificationApi['error']({
                            message: 'Failed to create schedule. Please try again.',
                            description: (<>
                                <Paragraph>
                                    Customer: {getCompanyByUsername(data.customer)}<br/>
                                    Device{failedDevices.length > 1 ? 's' :''}: {failedDevices.join(', ')}<br/>
                                    Scheduled date & time: {dayjs(data.dateTime).format(DATE_TIME_FORMAT)}
                                </Paragraph>
                            </>),
                            duration: 12,
                            style: {
                                width: 500,
                            },
                        });
                    }
                }, 1500) 
            } else {
                notificationApi['error']({
                    message: 'Error',
                    description: "Failed to create schedule. Please try again."
                });
            }
        })
    }

    const onValuesChange = (changedValues, allValues) => {
        if (changedValues.hasOwnProperty("customer")){
            form.setFieldsValue({ site: '', devices: [], fileName: ''})
            requestPlaces({username : changedValues.customer }).then( function(response) {
                setSites(response);
            })
        }

        if (changedValues.hasOwnProperty("site")){
            form.setFieldsValue({devices: [] })
            requestDevices({placeID : changedValues.site.toString()}).then( function(response) {
                createTree(response);
            })
        }
    }

    const handleQuickSelect = (minutes) => {
        form.setFieldsValue({
            dateTime: dayjs().add(minutes, 'minute')
        })
    }

    const handleMidnight = () => {
        form.setFieldsValue({
            dateTime: dayjs().add(1, "day").hour(0).minute(0).second(0).millisecond(0)
        })
    }

    return (
        <>
            <Form 
                form={form} 
                name="scheduler-form" 
                layout="vertical"
                onFinish={(values) => onFinish(values)}
                onValuesChange={(changedValues, allValues) => onValuesChange(changedValues, allValues)}
                >
                <Flex vertical >
                    <Flex wrap gap='middle' justify='space-between' style={{width: '100%'}}>
                        <Form.Item label='Customer' name='customer' {...schedulerCommonProps}>
                            <Select
                                placeholder= "Select Customer"
                                name="customer"
                                optionFilterProp='children'
                                showSearch
                                >
                                {customers?.sort((a, b) => a.companyName?.localeCompare(b.companyName)).map((customer) => (
                                    <Option key={customer.id} value={customer.username}>{customer.companyName}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                        
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.customer !== cur.customer}>
                            {({ getFieldValue }) => {
                                const hasCustomer = getFieldValue('customer');
                                return (
                                    <Form.Item name='site' label='Site' {...schedulerCommonProps}>
                                        <Select placeholder='Select a site' showSearch optionFilterProp='children' disabled={!hasCustomer}>
                                            { sites?.map((site) => (
                                                <Option key={site.id} value={site.id}>{site.name}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                )
                            }} 
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.site !== cur.site}>
                            {({ getFieldValue }) => {
                                const hasSite = getFieldValue('site');
                                return (
                                    <Form.Item name='devices' label='Device' {...schedulerCommonProps}>
                                        <TreeSelect
                                            showSearch
                                            disabled={!hasSite}
                                            multiple
                                            value={value}
                                            styles={{
                                                popup: {
                                                    root: {
                                                        maxHeight: 300,
                                                        overflow: 'auto',
                                                    },
                                                },
                                            }}
                                            treeData={devices}
                                            placeholder="Select Device"
                                            treeDefaultExpandAll
                                            onChange={(value, label, extra) => {onTreeChange(value, label, extra)}}
                                        />
                                    </Form.Item>
                                )
                            }} 
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.devices !== cur.devices}>
                            {({ getFieldValue }) => {
                                const hasDevices = getFieldValue('devices')?.length > 0;
                                return (
                                    <Form.Item 
                                        name="fileName"
                                        label="Update File"
                                        {...schedulerCommonProps}
                                        >
                                        <Select placeholder="Select File" showSearch optionFilterProp='children' disabled={!hasDevices}>
                                            {dataSource?.files?.map((file) => (
                                                <Option key={file}>{file}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                )
                            }}
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.fileName !== cur.fileName}>
                            {({ getFieldValue }) => {
                                const hasFile = getFieldValue('fileName');
                                return (
                                    <Form.Item  
                                        name="dateTime"
                                        label="Date and Time"
                                        tooltip={'Choose a time based on your local timezone'}
                                        {...schedulerCommonProps}
                                        >
                                        <DatePicker
                                            style={{width: '100%'}}
                                            showTime={{format: "HH:mm"}}
                                            open={openDatePicker}
                                            onOpenChange={setOpenDatePicker}
                                            onCalendarChange={(date) => {if(date) setPanelDate(date)}}
                                            value={scheduledDate}
                                            onChange={(date) => setScheduledDate(date)}
                                            showNow={false}
                                            disabled={!hasFile}
                                            disabledDate={(current) => {return current && current < dayjs().startOf('day')}}
                                            disabledTime={(date) => {
                                                if(!date || !date.isSame(dayjs(), 'day')) return {};
                                                return {
                                                    disabledHours: () => Array.from({ length: minTime.hour() }, (_, i) => i),
                                                    disabledMinutes: (hour) => {
                                                        if(hour === minTime.hour()){
                                                            const mins = [];
                                                            for (let m = 0; m < minTime.minute(); m++){
                                                                mins.push(m);
                                                            }
                                                            return mins;
                                                        }
                                                        return [];
                                                    },
                                                }
                                            }}
                                            renderExtraFooter={() => displayQuickSelect ? 
                                                <Flex gap='small' style={{padding: "12px 0"}}>
                                                    <Button size='small' color='primary' variant='outlined' onClick={() => {handleQuickSelect(2)}}>Earliest</Button>
                                                    <Button size='small' color='primary' variant='outlined' onClick={() => handleQuickSelect(5)}>+5 min</Button>
                                                    <Button size='small' color='primary' variant='outlined' onClick={() => handleQuickSelect(30)}>+30 min</Button>
                                                    <Button size='small' color='primary' variant='outlined' onClick={() => handleMidnight()}>Midnight</Button>
                                                </Flex>
                                                : 
                                                null
                                            }
                                        />
                                    </Form.Item>
                                )}}
                            </Form.Item>
                    </Flex>
                    <Flex gap='small' justify='end' align='center'>
                        <Form.Item noStyle>
                            <Button onClick={() => form.resetFields()}>Cancel</Button>
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue, isFieldTouched }) => (
                                <Button type="primary" htmlType="submit" disabled={!getFieldValue('dateTime' || !isFieldTouched('dateTime'))}>Create Schedule</Button>
                            )}
                        </Form.Item>
                    </Flex>
                </Flex>
            </Form>
        </>
    )
}